// @ts-strict-ignore
import invariant from 'invariant';
import { Dispatch } from 'redux';

import { fetchUserEnvironment } from 'api/apiUserEnvironment';
import {
  MainStoreSearchRadiusState,
  setSearchRadius as setSearchRadiusBase,
} from 'modules/main/store/ducks/searchRadius';
import {
  MainStoreUserEnvironmentState,
  updateUserEnvironment,
} from 'modules/main/store/ducks/userEnvironment';
import algoliaSearch from 'utils/algoliaSearch';
import {
  EDUCATION_LEVELS,
  EMPTY_SEARCH_LOCATION,
  LOCALES,
  ORG_TYPES,
  ORG_TYPES_ALLOW_TO_POST_VOLOPS,
  PROFESSIONAL_LEVELS,
  STATUSES_FOR_FILTERING,
  getDefaultSearchRadius,
} from 'utils/constants';
import { isDefined, updateObject } from 'utils/functional';
import { getJson } from 'utils/http';
import getSearchConfig from 'utils/search/getSearchConfig';
import { getStaffSearchQueryOptions } from 'utils/search/getSearchQueryOptions';
import {
  initialFacetFiltersForMainSearch,
  sideFacetsForType,
} from 'utils/search/searchFacets';
import { levelOptionTitles, levelOptions } from 'utils/search/sidefilters';
import { truncateToUTF8Bytes } from 'utils/string';

// Types

type InitializeAction = {
  type: 'SEARCH_INITIALIZE_STAFF';
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryParams: Record<string, any>;
};

type ResetPageAction = {
  type: 'SEARCH_RESET_PAGE_STAFF';
};

type SetQueryAction = {
  type: 'SEARCH_SET_QUERY_STAFF';
  query: string;
};

type SetPageIndexAction = {
  type: 'SEARCH_SET_PAGE_INDEX_STAFF';
  pageIndex: number;
};

type SetLocationAction = {
  type: 'SEARCH_SET_LOCATION_STAFF';
  location: SearchLocation;
};

type ClearLocationAction = {
  type: 'SEARCH_CLEAR_LOCATION_STAFF';
};

type ResetFiltersAction = {
  type: 'SEARCH_RESET_FILTERS_STAFF';
};

type SetFilterAction = {
  type: 'SEARCH_SET_FILTER_STAFF';
  key: string;
  value: unknown;
};

type SelectFilterOptionAction = {
  type: 'SEARCH_SELECT_FILTER_OPTION_STAFF';
  key: string;
  option: string | null | undefined;
};

type DeselectFilterOptionAction = {
  type: 'SEARCH_DESELECT_FILTER_OPTION_STAFF';
  key: string;
  option: string | null | undefined;
};

type SelectFilterSpecificOptionsAction = {
  type: 'SEARCH_SELECT_FILTER_SPECIFIC_OPTIONS_STAFF';
  key: string;
  options: Array<string>;
};

type SelectAllFilterOptionsAction = {
  type: 'SEARCH_SELECT_ALL_FILTER_OPTIONS_STAFF';
  key: string;
};

type SetSearchFacetOptionsAction = {
  type: 'SET_SEARCH_FACET_OPTIONS_STAFF';
  options: Array<string>;
  optionTitles: Array<string>;
  facetKey: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  optionRankings: Record<string, any>;
};

type ClearResultsAction = {
  type: 'SEARCH_CLEAR_RESULTS_STAFF';
};

type SearchStartedAction = {
  type: 'SEARCH_STARTED_STAFF';
};

type SuccessAction = {
  type: 'SEARCH_RESULTS_SUCCESS_STAFF';
  results: SearchResults;
};

type FetchPageSuccessAction = {
  type: 'SEARCH_FETCH_PAGE_SUCCESS_STAFF';
  results: SearchResults;
};

export type StaffStoreSearchAction =
  | InitializeAction
  | ResetPageAction
  | SetQueryAction
  | SetPageIndexAction
  | SetLocationAction
  | ClearLocationAction
  | ResetFiltersAction
  | SetFilterAction
  | SelectFilterOptionAction
  | DeselectFilterOptionAction
  | SelectFilterSpecificOptionsAction
  | SelectAllFilterOptionsAction
  | SetSearchFacetOptionsAction
  | ClearResultsAction
  | SearchStartedAction
  | SuccessAction
  | FetchPageSuccessAction;

// Reducer

const orgTypeFacet: SearchFacet = {
  id: 'orgTypeFacet',
  name: 'orgType',
  title: getText('Org Type'),
  type: 'array',
  options: levelOptions(ORG_TYPES),
  optionTitles: levelOptionTitles(ORG_TYPES),
};

const searchFacets: Record<string, SearchFacet> = {
  employmentTypeFacet: {
    id: 'employmentTypeFacet',
    name: 'jobType',
    title: getText('Job Type'),
    type: 'array',
    options: ['FULL_TIME', 'PART_TIME', 'TEMPORARY', 'CONTRACT'],
    optionTitles: {
      FULL_TIME: getText('Full Time'),
      PART_TIME: getText('Part Time'),
      TEMPORARY: getText('Temporary'),
      CONTRACT: getText('Contract'),
    },
  },
  educationFacet: {
    id: 'educationFacet',
    name: 'education',
    title: getText('Education Level'),
    type: 'array',
    options: levelOptions(EDUCATION_LEVELS),
    optionTitles: levelOptionTitles(EDUCATION_LEVELS),
  },
  professionalLevelFacet: {
    id: 'professionalLevelFacet',
    name: 'professionalLevel',
    title: getText('Experience Level'),
    type: 'array',
    options: levelOptions(PROFESSIONAL_LEVELS),
    optionTitles: levelOptionTitles(PROFESSIONAL_LEVELS),
  },
  localeFacet: {
    id: 'localeFacet',
    name: 'locale',
    title: getText('Listing Language'),
    type: 'array',
    options: levelOptions(LOCALES),
    optionTitles: levelOptionTitles(LOCALES),
  },
  orgTypeFacet,
  volopOrgTypeFacet: {
    ...orgTypeFacet,
    id: 'volopOrgTypeFacet',
    options: levelOptions(ORG_TYPES_ALLOW_TO_POST_VOLOPS),
    optionTitles: levelOptionTitles(ORG_TYPES_ALLOW_TO_POST_VOLOPS),
  },
  statusFacet: {
    id: 'statusFacet',
    name: 'status',
    title: getText('Status'),
    type: 'array',
    options: levelOptions(STATUSES_FOR_FILTERING),
    optionTitles: levelOptionTitles(STATUSES_FOR_FILTERING),
  },
  jobKeywordsFacet: {
    id: 'jobKeywordsFacet',
    lazy: true,
    name: 'functions',
    title: getText('Job Function'),
    type: 'array',
    options: [],
    // Lazy loaded
    optionTitles: {}, // Lazy loaded
  },
  internshipKeywordsFacet: {
    id: 'internshipKeywordsFacet',
    lazy: true,
    name: 'functions',
    title: getText('Functions'),
    type: 'array',
    options: [],
    // Lazy loaded
    optionTitles: {}, // Lazy loaded
  },
  volopKeywordsFacet: {
    id: 'volopKeywordsFacet',
    lazy: true,
    name: 'functions',
    title: getText('Functions'),
    type: 'array',
    options: [],
    // Lazy loaded
    optionTitles: {}, // Lazy loaded
  },
  orgAreasOfFocusFacet: {
    id: 'orgAreasOfFocusFacet',
    lazy: true,
    name: 'areasOfFocus',
    title: getText('Issue Areas'),
    type: 'array',
    options: [],
    // Lazy loaded
    optionTitles: {}, // Lazy loaded
  },
};

const DEFAULT_TYPE_FACET = 'ALL';
const initialFacetsThatArentLoaded = Object.keys(searchFacets).reduce(
  (acc: Array<string>, key) => {
    const facet = searchFacets[key];
    if (facet.lazy) return [...acc, key];
    return acc;
  },
  []
);

export const staffSearchTypes: StaffTypeFilter[] = [
  'JOB',
  'INTERNSHIP',
  'VOLOP',
  'ORG',
  'GRAD_PROGRAM',
  'USER',
  'INVOICE',
  'PAYMENT',
  'DONATION',
  'ALL',
];

export type SortType = 'relevance' | 'newest';

// @ts-expect-error ts-migrate(2456) FIXME: Type alias 'SearchResults' circularly references i... Remove this comment to see the full error message
export type SearchResults = SearchResults & {
  searchLocation: SearchLocation;
};

export type StaffStoreSearchState = {
  query: string;
  pageIndex: number;
  lastLoadedPageIndex: number;
  searchId: number;
  resultsByPage: Record<string, SearchResults>;
  resultsForSearchId?: string | null | undefined;
  initialized: boolean;
  initialSearchStarted: boolean;
  initialResultsLoaded: boolean;
  isSearching: boolean;
  searchLocation: SearchLocation;
  searchFacets: Record<string, SearchFacet>;
  facetsThatArentLoaded: Array<string>;
  filters: SearchFiltersWithType<StaffTypeFilter>;
  queryID: string | null | undefined;
};
const initialState = {
  query: '',
  pageIndex: 0,
  lastLoadedPageIndex: 0,
  searchId: 0,
  // Incrementing id
  resultsByPage: {},
  initialized: false,
  initialSearchStarted: false,
  initialResultsLoaded: false,
  isSearching: false,
  searchLocation: EMPTY_SEARCH_LOCATION,
  searchFacets,
  // Dont render search results that filter based on these facets until this is
  // loaded
  facetsThatArentLoaded: initialFacetsThatArentLoaded,
  filters: {},
  queryID: null,
};

export function searchReducer(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: StaffStoreSearchState = initialState,
  action: StaffStoreSearchAction
) {
  switch (action.type) {
    case 'SEARCH_INITIALIZE_STAFF': {
      const { page, q, type } = action.queryParams;
      const query = Array.isArray(q) ? q[0] : q;
      return {
        ...state,
        initialized: true,
        query: query ? truncateToUTF8Bytes(query || '', 512) : state.query,
        pageIndex: page ? Number(page) - 1 : state.pageIndex,
        filters: initialFacetFiltersForMainSearch(
          // This should be handled in the searchFacet reducer
          // If there is new search facets we need to update initialSearchFacets
          state.searchFacets,
          state.facetsThatArentLoaded,
          { ...action.queryParams, type: type || DEFAULT_TYPE_FACET }
        ),
      };
    }

    case 'SEARCH_RESET_PAGE_STAFF': {
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0, // New search, reset page
      };
    }

    case 'SEARCH_SET_QUERY_STAFF': {
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        query: action.query,
      };
    }

    case 'SEARCH_SET_PAGE_INDEX_STAFF': {
      return { ...state, pageIndex: action.pageIndex };
    }

    case 'SEARCH_SELECT_FILTER_OPTION_STAFF': {
      const facet = action.key;
      const filter = state.filters[facet];
      const selectedOptions = [
        ...((Array.isArray(filter) && filter) || []),
        action.option,
      ];
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        filters: { ...state.filters, [facet]: selectedOptions },
      };
    }

    case 'SEARCH_RESET_FILTERS_STAFF': {
      const type = staffSearchTypes.find((i) => i === state.filters.type);
      invariant(type, 'type must be defined');
      const filters = initialFacetFiltersForMainSearch(
        state.searchFacets,
        state.facetsThatArentLoaded,
        {
          type,
        }
      );
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        filters,
      };
    }

    case 'SEARCH_SET_FILTER_STAFF': {
      const facet = action.key;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line prefer-destructuring
      const value = action.value;

      const filters = (() => {
        if (facet === 'type' && isDefined(value)) {
          const t = staffSearchTypes.find((i) => i === value);
          invariant(t, 'type must be within main search types');
          return initialFacetFiltersForMainSearch(
            state.searchFacets,
            state.facetsThatArentLoaded,
            {
              type: t,
            }
          );
        }

        return updateObject(state.filters, {
          [facet as string]: value,
        });
      })();

      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        filters,
      };
    }

    case 'SEARCH_DESELECT_FILTER_OPTION_STAFF': {
      const facet = action.key;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line prefer-destructuring
      const option = action.option;
      invariant(Array.isArray(state.filters[facet]), 'Missing search filter');
      const selectedOptions = state.filters[facet].filter((v) => v !== option);
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        filters: { ...state.filters, [facet]: selectedOptions },
      };
    }

    case 'SEARCH_SELECT_FILTER_SPECIFIC_OPTIONS_STAFF': {
      const facet = action.key;
      invariant(Array.isArray(state.filters[facet]), 'Missing search filter');
      const selectedOptions = action.options;
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        landingPagePath: null,
        filters: { ...state.filters, [facet]: selectedOptions },
      };
    }

    case 'SEARCH_SELECT_ALL_FILTER_OPTIONS_STAFF': {
      const facet = action.key;
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        filters: {
          ...state.filters,
          [facet]: state.searchFacets[facet].options,
        },
      };
    }

    case 'SET_SEARCH_FACET_OPTIONS_STAFF': {
      const { options, optionTitles, facetKey, optionRankings } = action;
      const facet = state.searchFacets[facetKey];
      const facetsThatArentLoaded: Array<string> =
        state.facetsThatArentLoaded.filter((key) => key !== facetKey);
      const sideFacets = sideFacetsForType(searchFacets, state.filters.type);
      const hasFacet = sideFacets.find((f) => f.id === facet.id);
      const filterValue = facet.id && state.filters[facet.id];
      const updatedFilters =
        facet.id && hasFacet
          ? { ...state.filters, [facet.id]: filterValue || options }
          : state.filters;
      return {
        ...state,
        // This needs to get bumped if we change filter values
        // But if we dont, then it doesnt need to get changed
        searchId:
          filterValue && filterValue !== options
            ? state.searchId + 1
            : state.searchId,
        facetsThatArentLoaded,
        searchFacets: {
          ...state.searchFacets,
          [facetKey]: { ...facet, options, optionTitles, optionRankings },
        },
        // Only set the filter value if the current filter.type supports this
        // facet
        filters: updatedFilters,
      };
    }

    case 'SEARCH_STARTED_STAFF': {
      return {
        ...state,
        initialSearchStarted: true,
        isSearching: true,
        queryID: null,
      };
    }

    case 'SEARCH_RESULTS_SUCCESS_STAFF': {
      // If the results are from an older search, ignore them
      const lastResults =
        state.resultsByPage[state.lastLoadedPageIndex.toString()];

      if (lastResults && lastResults.timestamp > action.results.timestamp) {
        return state;
      }

      const newSearch = action.results.searchId !== state.resultsForSearchId;
      return {
        ...state,
        queryID: action.results.queryID,
        resultsByPage: newSearch
          ? {
              [action.results.page]: action.results,
            }
          : { ...state.resultsByPage, [action.results.page]: action.results },
        lastLoadedPageIndex: action.results.page,
        resultsForSearchId: action.results.searchId,
        initialResultsLoaded: true,
        isSearching: false,
      };
    }

    case 'SEARCH_FETCH_PAGE_SUCCESS_STAFF': {
      return {
        ...state,
        resultsByPage: {
          ...state.resultsByPage,
          [action.results.page]: action.results,
        },
        isSearching: false,
      };
    }

    case 'SEARCH_CLEAR_RESULTS_STAFF': {
      return { ...state, resultsByPage: {}, resultsForSearchId: null };
    }

    case 'SEARCH_SET_LOCATION_STAFF':
      return { ...state, searchLocation: action.location };

    case 'SEARCH_CLEAR_LOCATION_STAFF':
      return { ...state, searchLocation: EMPTY_SEARCH_LOCATION };

    default:
      return state;
  }
}

// Actions

const searchResultsSuccessful = ({
  results,
}: {
  results: SearchResults;
}): SuccessAction => ({
  type: 'SEARCH_RESULTS_SUCCESS_STAFF',
  results,
});

const executeSearchQuery = (
  {
    state: {
      search,
      userEnvironment,
      searchRadius: { value: searchRadius },
    },
    dispatch,
  }: {
    state: {
      search: StaffStoreSearchState;
      userEnvironment: MainStoreUserEnvironmentState;
      searchRadius: MainStoreSearchRadiusState;
    };
    dispatch: Dispatch<StaffStoreSearchAction>;
  } // TODO: Fix this the next time the file is edited.
): // eslint-disable-next-line @typescript-eslint/no-explicit-any
Promise<any> => {
  const timestamp = new Date();
  const queryOptions = getStaffSearchQueryOptions(search.searchFacets, {
    location: search.searchLocation,
    radius: searchRadius,
    ...search,
  });
  const {
    environment: {
      extraData: { staffSearchApiKey },
    },
  } = userEnvironment;

  const searchConfig = getSearchConfig(staffSearchApiKey);
  return algoliaSearch(searchConfig, search.query, queryOptions, true, () =>
    fetchUserEnvironment().then((fetchedUserEnvironment) => {
      // @ts-expect-error fix types
      dispatch(updateUserEnvironment(fetchedUserEnvironment));
      return fetchedUserEnvironment.environment.extraData.staffSearchApiKey;
    })
  ).then((res) => ({
    // Add a timestamp to keep track of when the query was executed
    ...res,
    // Add the searchLocation so we know which location the results are for
    searchLocation: search.searchLocation,
    searchId: search.searchId,
    timestamp,
    // This is for analytics
    searchParameters: {
      query: search.query,
      queryOptions,
    },
  }));
};

const loadDynamicFacetOptions =
  () =>
  (
    dispatch: Dispatch<StaffStoreSearchAction>,
    getState: () => { search: StaffStoreSearchState }
  ) => {
    const { facetsThatArentLoaded } = getState().search;
    return Promise.all(
      [
        {
          facetUrlSegment: 'job-functions',
          facetKey: 'jobKeywordsFacet',
          hasLang: true,
          responseToOptions: ({ jobFunctions }) => jobFunctions,
        },
        {
          facetUrlSegment: 'areas-of-focus',
          facetKey: 'orgAreasOfFocusFacet',
          hasLang: true,
          responseToOptions: ({ areasOfFocus }) => areasOfFocus,
        },
        {
          facetUrlSegment: 'volop-functions',
          facetKey: 'volopKeywordsFacet',
          hasLang: true,
          responseToOptions: ({ volopFunctions }) => volopFunctions,
        },
        {
          facetUrlSegment: 'internship-functions',
          facetKey: 'internshipKeywordsFacet',
          hasLang: true,
          responseToOptions: ({ internshipFunctions }) => internshipFunctions,
        },
      ].map(({ facetUrlSegment, facetKey, hasLang, responseToOptions }) => {
        if (!facetsThatArentLoaded.includes(facetKey)) {
          return Promise.resolve();
        }

        return getJson(
          `/data/website/${facetUrlSegment}${
            hasLang ? `?lang=${CURRENT_LOCALE}` : ''
          }`
        ).then((response) => {
          // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
          const options = responseToOptions(response);
          dispatch({
            type: 'SET_SEARCH_FACET_OPTIONS_STAFF',
            facetKey,
            options: options.map((f) => f.id),
            optionTitles: options.reduce(
              (sum, { id, text }) => ({ ...sum, [id]: text }),
              {}
            ),
            optionRankings: options.reduce(
              (sum, { id, usageRanking }) => ({ ...sum, [id]: usageRanking }),
              {}
            ),
          });
        });
      })
    );
  };

export const initializeSearch =
  (
    action: Record<string, any> = {} // eslint-disable-line @typescript-eslint/no-explicit-any
  ) =>
  (dispatch: Dispatch<StaffStoreSearchAction>) =>
    Promise.all([
      dispatch(
        // @ts-expect-error fix types
        {
          type: 'SEARCH_INITIALIZE_STAFF',
          ...action,
        }
      ),
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(loadDynamicFacetOptions()),
    ]);

export const searchResetPage = (): ResetPageAction => ({
  type: 'SEARCH_RESET_PAGE_STAFF',
});

export const setSearchQuery = (query: string) => ({
  type: 'SEARCH_SET_QUERY_STAFF',
  query,
});

export const setSearchRadius =
  (radius: string, didUserChange: boolean) =>
  (dispatch: Dispatch<StaffStoreSearchAction>) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch<any>(setSearchRadiusBase(radius, didUserChange));
    dispatch(searchResetPage());
  };

export const setPageIndex = (pageIndex: number) => ({
  type: 'SEARCH_SET_PAGE_INDEX_STAFF',
  pageIndex,
});

export const updateSearchLocation = (location: SearchLocation) => ({
  type: 'SEARCH_SET_LOCATION_STAFF',
  location,
});

export const clearSearchLocation = () => ({
  type: 'SEARCH_CLEAR_LOCATION_STAFF',
});

export const resetSearchFilters = (): ResetFiltersAction => ({
  type: 'SEARCH_RESET_FILTERS_STAFF',
});

export const clearAllFilters =
  () => (dispatch: Dispatch<StaffStoreSearchAction>) => {
    dispatch(resetSearchFilters());
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch<any>(setSearchRadius(getDefaultSearchRadius('relevance'), false));
  };

export const setSearchFilter = (
  filterKey: string,
  value: unknown | null | undefined
) => {
  if (filterKey === 'type') {
    invariant(value, 'type can never be set to undefined/null');
  }

  return {
    type: 'SEARCH_SET_FILTER_STAFF',
    key: filterKey,
    value,
  };
};

export const selectSearchFilterOption = (
  filterKey: string,
  option: string | null | undefined
) => ({
  type: 'SEARCH_SELECT_FILTER_OPTION_STAFF',
  key: filterKey,
  option,
});

export const deselectSearchFilterOption = (
  filterKey: string,
  option: string | null | undefined
) => ({
  type: 'SEARCH_DESELECT_FILTER_OPTION_STAFF',
  key: filterKey,
  option,
});

export const selectAllSearchFilterOptions = (filterKey: string) => ({
  type: 'SEARCH_SELECT_ALL_FILTER_OPTIONS_STAFF',
  key: filterKey,
});

export const selectSearchFilterSpecificOptions = (
  filterKey: string,
  options: Array<string>
) => ({
  type: 'SEARCH_SELECT_FILTER_SPECIFIC_OPTIONS_STAFF',
  key: filterKey,
  options,
});

export const search =
  () =>
  (
    dispatch: Dispatch<StaffStoreSearchAction>,
    getState: () => {
      search: StaffStoreSearchState;
      userEnvironment: MainStoreUserEnvironmentState;
      searchRadius: MainStoreSearchRadiusState;
    }
  ) => {
    dispatch({
      type: 'SEARCH_STARTED_STAFF',
    });
    const state = getState();
    executeSearchQuery({
      state,
      dispatch,
    })
      .then((results) => {
        dispatch(
          searchResultsSuccessful({
            results,
          })
        );
      })
      .catch((error) => error);
  };

export const clearResults = (): ClearResultsAction => ({
  type: 'SEARCH_CLEAR_RESULTS_STAFF',
});
